import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

// RentReceiptDto (Módulo 5 [5.3.2]) — rascunho de recibo de renda gerado quando
// uma renda é marcada como paga. camelCase pinado a partir do contrato do backend.
export interface RentReceipt {
  id: number;
  rentNoticeId: number;
  rentalContractId: number;
  receiptNumber: string;
  issueDate: string;
  paidDate: string;
  periodYear: number;
  periodMonth: number;
  periodLabel: string;
  amount: number;
  landlordName: string;
  landlordTaxId: string | null;
  tenantName: string;
  tenantTaxId: string | null;
  propertyName: string;
  propertyAddress: string | null;
  // Estado do recibo: "rascunho" | "emitido".
  status: string;
  notes: string | null;
}

// Filtros da lista — GET /api/rent-receipts.
export interface RentReceiptFilters {
  year?: number;
  month?: number;
  status?: string;
  rentNoticeId?: number;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return (await response.json()) as T;
}

export function getRentReceipts(
  filters?: RentReceiptFilters,
): Promise<RentReceipt[]> {
  const search = new URLSearchParams();
  if (filters?.year != null) {
    search.set("year", String(filters.year));
  }
  if (filters?.month != null) {
    search.set("month", String(filters.month));
  }
  if (filters?.status && filters.status.trim() !== "") {
    search.set("status", filters.status.trim());
  }
  if (filters?.rentNoticeId != null) {
    search.set("rentNoticeId", String(filters.rentNoticeId));
  }
  const query = search.toString();
  return request<RentReceipt[]>(`/api/rent-receipts${query ? `?${query}` : ""}`);
}

// Gera (ou devolve o existente — idempotente) o rascunho de recibo de um aviso pago.
export function generateRentReceipt(rentNoticeId: number): Promise<RentReceipt> {
  return request<RentReceipt>("/api/rent-receipts/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rentNoticeId }),
  });
}

export function setRentReceiptStatus(
  id: number,
  status: string,
): Promise<RentReceipt> {
  return request<RentReceipt>(`/api/rent-receipts/${id}/status`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
}

export async function deleteRentReceipt(id: number): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/api/rent-receipts/${id}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}
